// Pure helper functions for the cost code summary section.
// No DOM or Kendo dependencies - Jest-testable in isolation.

const FTE_DIVISOR = 173.34;

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const LABOR_CODES = [
    { hourCode: 3, costCode: 30, name: 'Project Control' },
    { hourCode: 4, costCode: 40, name: 'BIM' },
    { hourCode: 5, costCode: 50, name: 'Shop Labor' },
    { hourCode: 6, costCode: 60, name: 'Field Labor' },
];

/**
 * Computes FTE from hours: hours / 173.34, rounded to 2 decimal places.
 * @param {number} hours
 * @returns {number}
 */
export const computeFte = (hours) =>
    Math.round((hours / FTE_DIVISOR) * 100) / 100;

/**
 * Converts a YYYYMM integer month key to a "Mmmyy" string (e.g. 202601 -> "Jan26").
 * @param {number} monthKey
 * @returns {string}
 */
export const monthKeyToField = (monthKey) => {
    const yr = Math.floor(monthKey / 100);
    const mo = monthKey % 100;
    return MONTH_NAMES[mo - 1] + String(yr).slice(-2);
};

/**
 * Accumulates a value into a Values dict keyed by the month field string.
 * @param {Object} values - mutable dict to accumulate into
 * @param {number} monthKey - YYYYMM integer
 * @param {number|null} value
 */
const addToValues = (values, monthKey, value) => {
    if (value == null) return;
    const field = monthKeyToField(monthKey);
    values[field] = (values[field] ?? 0) + Number(value);
};

/**
 * Sums all values in a Values dict.
 * @param {Object} values
 * @returns {number}
 */
const sumValues = (values) =>
    Object.values(values).reduce((acc, v) => acc + v, 0);

/**
 * Groups a flat array of SP rows into the top-level display entries.
 * Includes a companion FTE sub-row for Labor Hours (marked with isSubRow).
 * Each entry has a Values dict keyed by "Mmmyy" strings, a total, and a format.
 * @param {Array} rows - flat rows from GetCostCodeSummary (one per project/costCode/month)
 * @returns {Array} 8-element array in display order
 */
export const buildTopLevelRows = (rows) => {
    const buildValues = (predicate, accessor) => {
        const values = {};
        rows.filter(predicate).forEach(r => addToValues(values, r.monthKey, accessor(r)));
        return values;
    };

    const byCostCode = (code) => (r) => r.costCode === code;
    const byAnyCostCode = (codes) => (r) => codes.includes(r.costCode);

    const laborHourCodes = LABOR_CODES.map(l => l.hourCode);
    const laborCostCodes = LABOR_CODES.map(l => l.costCode);

    const laborHoursValues = buildValues(byAnyCostCode(laborHourCodes), r => r.projectedHours);
    const totalLaborHours = sumValues(laborHoursValues);
    const laborHoursFteValues = Object.fromEntries(Object.entries(laborHoursValues).map(([k, v]) => [k, computeFte(v)]));

    return [
        {
            name: 'Equipment',
            section: 'equipment',
            format: 'currency',
            Values: buildValues(byCostCode(1), r => r.projectedCost),
            get total() { return sumValues(this.Values); },
        },
        {
            name: 'Material',
            section: 'material',
            format: 'currency',
            Values: buildValues(byCostCode(2), r => r.projectedCost),
            get total() { return sumValues(this.Values); },
        },
        {
            name: 'Labor Hours',
            section: 'laborHours',
            format: 'hours',
            Values: laborHoursValues,
            total: totalLaborHours,
        },
        {
            name: '',
            section: 'laborHours',
            format: 'fte',
            Values: laborHoursFteValues,
            total: computeFte(totalLaborHours),
            isSubRow: true,
        },
        {
            name: 'Labor Costs',
            section: 'laborCosts',
            format: 'currency',
            Values: buildValues(byAnyCostCode(laborCostCodes), r => r.projectedCost),
            get total() { return sumValues(this.Values); },
        },
        {
            name: 'Subcontractors',
            section: 'subcontractors',
            format: 'currency',
            Values: buildValues(byCostCode(7), r => r.projectedCost),
            get total() { return sumValues(this.Values); },
        },
        {
            name: 'Other Costs',
            section: 'otherCosts',
            format: 'currency',
            Values: buildValues(byCostCode(8), r => r.projectedCost),
            get total() { return sumValues(this.Values); },
        },
        {
            name: 'General Conditions',
            section: 'generalConditions',
            format: 'currency',
            Values: buildValues(byCostCode(9), r => r.projectedCost),
            get total() { return sumValues(this.Values); },
        },
    ];
};

/**
 * Returns per-project rows for a single cost code, each with a Values dict.
 * Uses projectedHours for labor hour codes (3-6), projectedCost for all others.
 * @param {Array} rows - flat rows from GetCostCodeSummary
 * @param {number} costCode
 * @returns {Array} one entry per unique projectNumber
 */
export const buildChildRows = (rows, costCode) => {
    const isHourCode = costCode >= 3 && costCode <= 6;
    const accessor = isHourCode ? r => r.projectedHours : r => r.projectedCost;

    const filtered = rows.filter(r => r.costCode === costCode);

    const projectMap = new Map();
    filtered.forEach(r => {
        if (!projectMap.has(r.projectNumber)) {
            projectMap.set(r.projectNumber, { values: {}, projectName: r.projectName });
        }
        addToValues(projectMap.get(r.projectNumber).values, r.monthKey, accessor(r));
    });

    return Array.from(projectMap.entries()).flatMap(([projectNumber, { values, projectName }]) => {
        const total = sumValues(values);
        const hoursRow = { projectNumber, projectName, Values: values, total, format: isHourCode ? 'hours' : 'currency' };
        if (!isHourCode) return [hoursRow];
        const fteValues = Object.fromEntries(Object.entries(values).map(([k, v]) => [k, computeFte(v)]));
        return [
            hoursRow,
            { projectNumber: '', projectName: '', Values: fteValues, total: computeFte(total), format: 'fte', isSubRow: true },
        ];
    });
};

/**
 * Groups labor rows into 4 sub-rows (one per labor type), each with a Values dict.
 * @param {Array} rows - flat rows from GetCostCodeSummary
 * @param {boolean} isLaborCost - true for cost variant (codes 30-60), false for hours variant (codes 3-6)
 * @returns {Array} 4-element array in labor code order
 */
export const buildLaborSubRows = (rows, isLaborCost) => {

    return LABOR_CODES.flatMap(({ hourCode, costCode, name }) => {
        const code = isLaborCost ? costCode : hourCode;
        const accessor = isLaborCost ? r => r.projectedCost : r => r.projectedHours;

        const values = {};
        rows.filter(r => r.costCode === code).forEach(r => addToValues(values, r.monthKey, accessor(r)));
        const total = sumValues(values);

        const mainRow = { name, costCode: code, Values: values, total, format: isLaborCost ? 'currency' : 'hours' };
        if (isLaborCost) return [mainRow];

        const fteValues = Object.fromEntries(Object.entries(values).map(([k, v]) => [k, computeFte(v)]));
        return [
            mainRow,
            { name: '', costCode: code, Values: fteValues, total: computeFte(total), format: 'fte', isSubRow: true },
        ];
    });
};
